// Execution-quality presentation logic (TASK-20260728-researcher-observability-ui, §10 realized-vs-modeled):
// the pure half of the Execution tab. Server aggregates (percentiles, status counts) come from the
// /execution-quality read; these helpers shape the row window for charts and the per-symbol table.

// The audit's modeled round-trip cost assumption (spec §7/§10) — the reference line realized slippage is
// measured against. Probed from the frozen params when a strategy records a numeric model; Shishito's
// descriptor honestly records `fees: "real_venue_fees"` (no model implemented), so the spec constant is
// the comparison target.
const SPEC_MODELED_BPS = 20;

export function modeledBps(versionResult, fallback = SPEC_MODELED_BPS) {
  const params = versionResult?.version?.params;
  const candidates = [
    params?.execution?.modeled_cost_bps,
    params?.modeled_cost_bps,
    params?.execution?.cost_bps,
    params?.cost_bps,
  ];
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) return c;
  }
  return fallback;
}

// Computable slippage samples only, time-ascending — nulls (cancelled-pre-fill rows) are absent, not zero.
export function slippagePoints(rows) {
  return (rows ?? [])
    .filter((r) => r.slippageBps !== null && r.slippageBps !== undefined)
    .map((r) => ({ time: r.barTime, value: Number(r.slippageBps) }))
    .sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
}

// Fixed-width buckets over the sample span. Empty sample → empty list (the tab renders the honest
// empty state, never a fake flat histogram).
export function slippageHistogram(values, bucketWidth = null) {
  const vs = (values ?? []).filter((v) => Number.isFinite(v));
  if (vs.length === 0) return [];
  const lo = Math.min(...vs);
  const hi = Math.max(...vs);
  if (lo === hi) return [{ lo, hi, count: vs.length }];
  const width = bucketWidth ?? Math.max(0.5, (hi - lo) / 12);
  const buckets = [];
  for (let b = lo; b < hi; b += width) {
    buckets.push({ lo: b, hi: b + width, count: 0 });
  }
  for (const v of vs) {
    const idx = Math.min(buckets.length - 1, Math.floor((v - lo) / width));
    buckets[idx].count += 1;
  }
  return buckets;
}

// Per-symbol quality rollup for the table. Mean slippage over computable samples only (null when none);
// fees sum treats null as no-fee (a cancelled order cost nothing).
export function perSymbolStats(rows) {
  const bySymbol = new Map();
  for (const r of rows ?? []) {
    const key = r.symbol ?? '';
    if (!bySymbol.has(key)) bySymbol.set(key, { symbol: key, count: 0, slippage: [], totalFees: 0 });
    const s = bySymbol.get(key);
    s.count += 1;
    if (r.slippageBps !== null && r.slippageBps !== undefined) s.slippage.push(Number(r.slippageBps));
    if (r.fees !== null && r.fees !== undefined) s.totalFees += Number(r.fees);
  }
  return [...bySymbol.values()]
    .map(({ symbol, count, slippage, totalFees }) => ({
      symbol,
      count,
      meanSlippageBps: slippage.length > 0 ? slippage.reduce((a, b) => a + b, 0) / slippage.length : null,
      totalFees,
    }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
}
